import { BaseLayout, ContainedRow } from '../../layouts';
import { IncomingMessage, ServerResponse } from 'http';

import { CategoryProducts } from '../../components/CategoryProducts';
import type { CategoryType } from '../../types/category';
import Cookies from 'cookies';
import { TopMainBanner } from '../../shared/TopMainBanner';
import { getMarketplace } from '../../api/marketplace';
import { getStoreId } from '../../api/server/getStoreId';
import { initialFetch } from '../../api/server/initialFetch';
import { retrieveProductsByCategory } from '../../api/products';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import type { state } from '../../types/store/state';
import type { staticProps } from '../../types/pages/props';
import { uiStateMap } from '../../utils/uiStateMap';
import type { uiType } from '../../types/ui';
import { useSelector } from 'react-redux';
import { useTranslation } from 'next-i18next';

interface Props {
  category: CategoryType;
}

const Category = (props: Props) => {
  const { t } = useTranslation();
  const metatags = useSelector((state: state) => state.ui.metatags);
  const domain = useSelector((state: state) => state.ui.domain);
  const storeName = useSelector((state: state) => state.ui.name);

  const breadcrumbs = [
    {
      name: 'Home',
      url: '/',
    },
    {
      name: props.category.name,
      url: `/category/${props.category.id}/`,
    },
  ];

  return (
    <BaseLayout
      {...metatags}
      domain={domain}
      storeName={storeName}
      title={`${storeName} | ${t('header.topMainBanner.cat.title')} ${
        props.category.name
      }`}
    >
      <TopMainBanner
        isCompact={true}
        title={`${t('header.topMainBanner.cat.title')} ${props.category.name}`}
        breadcrumbs={breadcrumbs}
      />
      <ContainedRow>
        <CategoryProducts categoryId={props.category.id} />
      </ContainedRow>
    </BaseLayout>
  );
};

export const getServerSideProps = async (context: staticProps) => {
  try {
    const translations = await serverSideTranslations(context.locale);
    const marketplace = await getMarketplace(context.req.headers.host);
    const cookies = new Cookies(context.req as unknown as IncomingMessage, context.res as ServerResponse);
    const sessionCookie = cookies.get('user_token');

    if (!marketplace.data.length) {
      return {
        props: {
          ...translations,
        },
        notFound: true,
      };
    }

    const marketplaceMap: uiType = uiStateMap(marketplace);
    const branchStoreId = await getStoreId(sessionCookie, marketplaceMap.storeId, marketplaceMap.businessId);

    if (branchStoreId === 0) {
      return {
        notFound: true,
      };
    }

    const response = await initialFetch(branchStoreId);

    let category = await response.categories.results.filter(
      (elem: CategoryType) => elem.id === Number(context.params.id)
    );

    if (category.length) {
      category = category[0];
    } else {
      return {
        notFound: true,
      };
    }

    let products;

    try {
      products = await retrieveProductsByCategory({
        id: marketplaceMap.storeId,
        category: category.id,
        page: context.query.page ? context.query.page : '1',
      });
    } catch (error: any) {
      if (error.response.status === 404) {
        return {
          notFound: true,
        };
      }
      throw new Error(error);
    }

    const preloadedState = JSON.parse(
      JSON.stringify({
        ui: marketplaceMap,
        categories: response.categories.results,
        branchStore: response.branchStoreDetail,
        branchStoreSchedule: response.branchStoreSchedule.results,
        products,
      })
    );

    return {
      props: {
        ...translations,
        preloadedState: preloadedState,
        category,
      },
    };
  } catch (error: any) {
    throw new Error(error);
  }
};

export default Category;
